import React, { useEffect, useState } from 'react';
import { Box, Image, Card, CardHeader, CardBody, Flex, Text, Spinner, Input, Button } from "@chakra-ui/react";
import axios from 'axios';
import ClockBar from '../Search/ClockBar';
import logo from "../../asset/EssilorLuxottica.svg";
import TimelineRow from './TimelineRow';
import { BASE_API } from '../TableAPI/Constant';

interface FileUploadData {
    _id: string;
    datetime_start: string;
    datetime_end: string;
    file: string;
    status: string;
}

const FileUpload: React.FC = () => {
    const [timelineData, setTimelineData] = useState<FileUploadData[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [searchTerm, setSearchTerm] = useState<string>('');
    
    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10; // Adjust this number as needed

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                const response = await axios.get(`${BASE_API}/logProcess/list/`);
                if (response.data && response.data.data) {
                    setTimelineData(response.data.data);
                } else {
                    throw new Error("Data not available.");
                }
            } catch (error) {
                console.error("Error retrieving data!", error);
                setError("Error retrieving data. Please try again later.");
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, []);

    const handleLogoClick = () => {
        window.location.href = '/search';
    };

    const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchTerm(event.target.value);
        setCurrentPage(1); // Reset to first page on search
    };

    const filteredData = timelineData.filter(row =>
        row.file.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Pagination logic
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = filteredData.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(filteredData.length / itemsPerPage);

    return (
        <Box>
            <Flex alignItems="center" justifyContent="space-between" mb={7} p={4} bg="gray.100" borderRadius="md" boxShadow="md">
                <Image src={logo} alt="Logo EssilorLuxottica" cursor="pointer" onClick={handleLogoClick} />
                <Box justifyContent="center" ml={-60}>
                    <ClockBar />
                </Box>
            </Flex>

            <Box display="flex" flexDirection="column" alignItems="center">
                <Input
                    placeholder="Type Project Name or Project Code"
                    value={searchTerm}
                    onChange={handleSearchChange}
                    bg="white"
                    color="black"
                    border="2px"
                    borderColor="gray.300"
                    _placeholder={{ color: '#BCBEC0' }}
                    p={3}
                    width={{ base: "100%", md: "600px", lg: "800px" }}
                    mb={4}
                />

                <Card p={3} borderRadius="md" boxShadow="lg" width={{ base: "100%", md: "600px", lg: "800px" }}>
                    <CardHeader pt="0px" p="12px 0px 4px 12px" backgroundImage="linear-gradient(to right, #E6E7E8, #BCBEC0)" borderTopRadius="md" textAlign="center">
                        <Text fontSize="xl" color="black" fontWeight="bold" mt={2} mb={2}>
                            File Progress
                        </Text>
                    </CardHeader>
                    <CardBody ps="20px" pe="0px" mb="15px">
                        <Box>
                            {loading ? (
                                <Box justifyContent="center" alignItems="center" height="100%">
                                    <Spinner size="lg" color="#007BFF" />
                                    <Text ml={3} color="#414042">Caricamento in corso...</Text>
                                </Box>
                            ) : error ? (
                                <Text color="#414042">{error}</Text>
                            ) : currentItems.length > 0 ? (
                                currentItems.map((row) => (
                                    <TimelineRow
                                        key={row._id}
                                        title={`${row.file} - ${row.status}`}
                                        startDate={row.datetime_start}
                                        endDate={row.datetime_end}
                                        color={row.status === "OK" ? "#115E42" : "#962324"}
                                    />
                                ))
                            ) : (
                                <Text color={'#962324'}>No data found.</Text>
                            )}
                        </Box>

                        {/* Pagination Controls */}
                        <Flex justifyContent="space-between" mt={4}>
                            <Button onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))} disabled={currentPage === 1}>
                                Previous
                            </Button>
                            <Text>{`Page ${currentPage} of ${totalPages}`}</Text>
                            <Button onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))} disabled={currentPage === totalPages}>
                                Next
                            </Button>
                        </Flex>
                    </CardBody>
                </Card>
            </Box>
        </Box>
    );
};

export default FileUpload; 